import { useEffect, useRef } from 'react';
import { observer } from '../../../constants';
import { FileMessageLink } from '../FileMessageLnk/FileMessageLink';
import styles from './LinksAttachments.module.scss';
import cx from 'classnames';
import { NoContent } from 'components';
import { useIntersectionObserver } from 'hooks';
import { PatientLink } from 'pages/Patient/components/PatientProfile/components/PatientLinks/components/PatientLink/PatientLink';
import {
  selectCurrentPatientCase,
  selectMessageAttachments,
  selectMessageAttachmentsInfo,
  selectMessageAttachmentsReqParams,
  selectPatientActionIsLoading,
} from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { AttachmentsTab } from 'types';

type Props = {
  type: AttachmentsTab;
};

export const LinksAttachments = ({ type }: Props) => {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const dispatch = useDispatch();
  const params = useSelector(selectMessageAttachmentsReqParams);
  const attachments = useSelector(selectMessageAttachments);
  const currentPatientCase = useSelector(selectCurrentPatientCase);
  const getMessageAttachments = useSelector(selectPatientActionIsLoading('getMessagesAttachments'));
  const attachmentsInfo = useSelector(selectMessageAttachmentsInfo);

  const patientCaseId = currentPatientCase?.id;

  useEffect(() => {
    if (!patientCaseId) return;
    dispatch(patientsActions.getMessagesAttachments({ patientCaseId, messageAttachmentParams: params }));
  }, [type, params]);

  const onIntersectionAction = () => {
    dispatch(
      patientsActions.setMessageAttachmentsRequestParams({
        ...params,
        page: params.page ? params.page + 1 : 1,
      }),
    );
  };

  useIntersectionObserver({
    info: attachmentsInfo,
    IntersectionObject: ref,
    IntersectionObserverProps: { rootMargin: observer.rootMargin },
    dependencies: [attachments],
    onIntersectionAction,
  });

  return (
    <div
      className={cx(styles.container, {
        [styles.noAttachments]: !attachments.length,
      })}>
      {attachments.length ? (
        attachments.map((el, index) => {
          return (
            <div
              className={cx(styles.linkWrapper, { [styles.link]: type === AttachmentsTab.LINKS })}
              key={el.messageId}>
              {type === AttachmentsTab.LINKS ? (
                <PatientLink link={el.link} ref={index === attachments.length - 1 ? ref : null} />
              ) : (
                <FileMessageLink
                  link={el.file?.url}
                  text={el.file?.name}
                  ref={index === attachments.length - 1 ? ref : null}
                />
              )}
            </div>
          );
        })
      ) : (
        <NoContent
          pageKey={type === AttachmentsTab.LINKS ? 'discussionLinks' : 'discussionFiles'}
          className={styles.noContent}
        />
      )}
    </div>
  );
};
